import { useEffect, useState } from "react";

const REACTIONS = [
  { value: 5, emoji: "🤩", label: "Excellent", color: "#f59e0b", bg: "#fef3c7", border: "#fbbf24" },
  { value: 4, emoji: "🙂", label: "Good",      color: "#16a34a", bg: "#dcfce7", border: "#4ade80" },
  { value: 3, emoji: "😐", label: "Neutral",   color: "#6366f1", bg: "#ede9fe", border: "#a5b4fc" },
  { value: 1, emoji: "🤬", label: "Angry",     color: "#dc2626", bg: "#fee2e2", border: "#fca5a5" },
];

const RATING_LABEL = { 5: "Excellent", 4: "Good", 3: "Neutral", 1: "Angry" };
const RATING_COLOR = { 5: "#f59e0b",   4: "#16a34a", 3: "#6366f1", 1: "#dc2626" };
const RATING_BG    = { 5: "#fef3c7",   4: "#dcfce7", 3: "#ede9fe", 1: "#fee2e2" };

export default function Feedback() {
  const [rating, setRating]       = useState(null);
  const [mobile, setMobile]       = useState("");
  const [loading, setLoading]     = useState(false);
  const [toast, setToast]         = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [feedbacks, setFeedbacks] = useState([]);

  const fetchFeedbacks = async () => {
    try {
      const token = localStorage.getItem("token");
      const searchParams = new URLSearchParams(window.location.search);
      const tenantId = searchParams.get("tenantId");

      let url = "/api/feedback";
      if (tenantId) {
        url += `?tenantId=${encodeURIComponent(tenantId)}`;
      }

      const headers: Record<string, string> = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const res = await fetch(url, { headers });
      const data = await res.json();
      if (data.success) {
        setFeedbacks(
          (data.rows || []).map((r: any) => ({
            ...r,
            id: r._id || r.id
          }))
        );
      } else {
        setFeedbacks([]);
      }
    } catch (err) {
      console.error("Fetch feedback error:", err);
      setFeedbacks([]);
    }
  };

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  const showToast = (title, desc, type = "success") => {
    setToast({ title, desc, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rating) { showToast("Rating required", "Please select a reaction.", "error"); return; }
    if (!mobile.trim()) { showToast("Mobile required", "Please enter your mobile number.", "error"); return; }
    if (!/^\+?\d[\d\s]{9,}$/.test(mobile)) {
      showToast("Invalid mobile", "Enter a valid mobile number.", "error"); return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const searchParams = new URLSearchParams(window.location.search);
      const tenantId = searchParams.get("tenantId");

      let url = "/api/feedback";
      if (tenantId) {
        url += `?tenantId=${encodeURIComponent(tenantId)}`;
      }

      const response = await fetch(url, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          ...(token ? { Authorization: `Bearer ${token}` } : {}) 
        },
        body: JSON.stringify({ 
          mobileNumber: mobile, 
          rating, 
          message: "User Feedback",
          ...(tenantId ? { tenantId } : {})
        }),
      });
      const result = await response.json();
      if (result.success) {
        await fetchFeedbacks();
        setSubmitted(true);
      } else {
        showToast("Error", result.error || "Submission failed.", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Error", "Failed to submit feedback", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setRating(null);
    setMobile("");
    setSubmitted(false);
  };

  const counts = REACTIONS.reduce((acc, r) => {
    acc[r.value] = (feedbacks || []).filter(f => f?.rating === r.value).length;
    return acc;
  }, {});

  const total = feedbacks?.length || 0;

  return (
    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif", background: "#f0f4f8", minHeight: "100vh", paddingBottom: 40 }}>

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", top: 20, right: 20, zIndex: 9999,
          background: toast.type === "error" ? "#dc2626" : "#16a34a",
          color: "#fff", borderRadius: 12, padding: "14px 20px",
          minWidth: 260, boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
        }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{toast.title}</div>
          <div style={{ fontSize: 12, opacity: 0.88 }}>{toast.desc}</div>
        </div>
      )}

      {/* Header */}
      <div style={{ background: "#1e40af", padding: "18px 32px", display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{ background: "rgba(255,255,255,0.18)", borderRadius: 10, width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
          💬
        </div>
        <div>
          <h1 style={{ margin: 0, color: "#fff", fontSize: 20, fontWeight: 600, letterSpacing: 0.2 }}>Customer Feedback</h1>
          <p style={{ margin: 0, color: "rgba(255,255,255,0.7)", fontSize: 13, marginTop: 2 }}>Rate your experience at our fuel station</p>
        </div>
      </div>

      {/* Page body */}
      <div style={{ maxWidth: 500, margin: "0 auto", padding: "28px 20px 0" }}>

        {/* 1 — Form card */}
        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 10px rgba(0,0,0,0.05)", marginBottom: 20 }}>

          <div style={{ background: "#1e40af", padding: "14px 22px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 16 }}>⭐</span>
              <span style={{ color: "#fff", fontWeight: 600, fontSize: 15 }}>Submit feedback</span>
            </div>
            <span style={{ background: "rgba(255,255,255,0.22)", color: "#fff", borderRadius: 20, padding: "3px 12px", fontSize: 11, fontWeight: 600, letterSpacing: 0.4 }}>
              ENTRY FORM
            </span>
          </div>

          {submitted ? (
            <div style={{ padding: "40px 24px", textAlign: "center" }}>
              <div style={{
                width: 58, height: 58, borderRadius: "50%",
                background: "#dcfce7", display: "flex", alignItems: "center",
                justifyContent: "center", margin: "0 auto 16px", fontSize: 24,
              }}>✓</div>
              <h2 style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 600, color: "#1e293b" }}>Thank you!</h2>
              <p style={{ margin: "0 0 24px", fontSize: 14, color: "#64748b" }}>Your feedback has been recorded successfully.</p>
              <button
                onClick={handleReset}
                style={{
                  background: "#f8fafc", border: "1px solid #e2e8f0",
                  borderRadius: 10, padding: "10px 24px",
                  fontSize: 14, color: "#374151", cursor: "pointer",
                }}
              >
                Submit another response
              </button>
            </div>
          ) : (
            <div style={{ padding: "24px 22px" }}>
              <p style={{ margin: "0 0 18px", fontSize: 13, color: "#64748b", textAlign: "center" }}>
                How was your experience today?
              </p>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 18 }}>
                {REACTIONS.map(r => (
                  <button
                    key={r.value}
                    onClick={() => setRating(r.value)}
                    style={{
                      display: "flex", flexDirection: "column", alignItems: "center", gap: 5,
                      padding: "16px 10px", borderRadius: 12, cursor: "pointer",
                      border: rating === r.value ? `2px solid ${r.border}` : "1.5px solid #e2e8f0",
                      background: rating === r.value ? r.bg : "#fafbfc",
                      transform: rating === r.value ? "scale(1.03)" : "scale(1)",
                      transition: "all 0.15s",
                      outline: "none",
                    }}
                  >
                    <span style={{ fontSize: 30, lineHeight: 1 }}>{r.emoji}</span>
                    <span style={{ fontSize: 12, fontWeight: 500, color: rating === r.value ? r.color : "#64748b" }}>
                      {r.label}
                    </span>
                  </button>
                ))}
              </div>

              {rating && (
                <div style={{ textAlign: "center", marginBottom: 16 }}>
                  <span style={{
                    display: "inline-block",
                    background: RATING_BG[rating],
                    color: RATING_COLOR[rating],
                    borderRadius: 20,
                    padding: "5px 16px",
                    fontSize: 13,
                    fontWeight: 600,
                    border: `1px solid ${RATING_COLOR[rating]}30`,
                  }}>
                    Selected: {RATING_LABEL[rating]}
                  </span>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <label style={{ display: "block", marginBottom: 6, fontSize: 11, fontWeight: 600, color: "#1e40af", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Mobile number *
                </label>
                <input
                  type="tel"
                  placeholder="+91 XXXXX XXXXX"
                  value={mobile}
                  onChange={e => setMobile(e.target.value)}
                  style={{
                    width: "100%", height: 42,
                    border: "1px solid #e2e8f0", borderRadius: 10,
                    padding: "0 14px", fontSize: 15, outline: "none",
                    boxSizing: "border-box", background: "#fff",
                    color: "#1e293b", marginBottom: 18,
                  }}
                />
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: "100%", height: 44,
                    background: loading ? "#94a3b8" : "#16a34a",
                    color: "#fff", border: "none", borderRadius: 10,
                    fontSize: 14, fontWeight: 600, letterSpacing: 0.4,
                    cursor: loading ? "default" : "pointer",
                    transition: "background 0.15s",
                  }}
                >
                  {loading ? "Submitting…" : "SEND FEEDBACK"}
                </button>
              </form>
            </div>
          )}
        </div>

        {/* Stats + Rating Row */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 2fr",
            gap: 12,
            marginBottom: 20,
            alignItems: "stretch",
          }}
        >

          {/* Total Responses */}
          <StatCard label="Total responses" value={total} />

          {/* Most Recent */}
          <StatCard
            label="Most recent"
            value={(total && feedbacks?.[0]?.rating) ? RATING_LABEL[feedbacks[0].rating] : "—"}
            small
          />

          {/* Rating Breakdown */}
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              border: "1px solid #e2e8f0",
              padding: "16px 18px",
            }}
          >
            <p
              style={{
                margin: "0 0 14px",
                fontSize: 11,
                fontWeight: 600,
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: 0.6,
              }}
            >
              Rating breakdown
            </p>

            {REACTIONS.map((r) => (
              <div
                key={r.value}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <span style={{ fontSize: 18, width: 22, textAlign: "center" }}>
                  {r.emoji}
                </span>

                <div
                  style={{
                    flex: 1,
                    height: 7,
                    background: "#f1f5f9",
                    borderRadius: 4,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: total
                        ? `${((counts[r.value] || 0) / total) * 100}%`
                        : "0%",
                      background: r.color,
                      borderRadius: 4,
                    }}
                  />
                </div>

                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#64748b",
                    minWidth: 18,
                    textAlign: "right",
                  }}
                >
                  {counts[r.value] || 0}
                </span>
              </div>
            ))}
          </div>

        </div>

      </div>
    </div>
  );
}

function StatCard({ label, value, small }) {
  return (
    <div style={{
      background: "#fff", borderRadius: 12,
      border: "1px solid #e2e8f0", padding: "16px 18px",
    }}>
      <p style={{ margin: "0 0 6px", fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6 }}>
        {label}
      </p>
      <p style={{ margin: 0, fontSize: small ? 16 : 26, fontWeight: 600, color: "#1e293b", paddingTop: small ? 4 : 0 }}>
        {value}
      </p>
    </div>
  );
}